import React, {
    ReactNode,
    ComponentType,
    HTMLAttributeAnchorTarget,
    useContext,
    useMemo,
    FunctionComponent,
    MouseEvent,
    useCallback,
    useRef,
    ForwardedRef,
    AriaRole,
    CSSProperties,
} from 'react';
import { mergeProps, PressEvent, useHover, useLink, usePress } from 'react-aria';
import { shouldClientNavigate, useRouter } from '@react-aria/utils';
import { genericForwardRef, IsNever } from '@direct-frontend/stdlib';
import { HoverEvents } from '@react-types/shared';
import { isDesktop, isMobile } from '@direct-frontend/platform-code';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';

import { ListBaseItem, ListBaseItemProps } from '../ListBaseItem/ListBaseItem';
import { LabelProps } from '../../Label/Label';
import { cn } from '../../../lib/css/className/className';
import { ListContext, getTextPresetWithSkin } from '../utils';
import { HelpHandler } from '../../Help/types';
import { HStack, VStack } from '../../Stack';
import { ColorlessIconProps, IconOpenInNewTab } from '../../../icons';
import { TestProps } from '../../../internal/types/testTypes';
import { ListIconRender } from '../ListIconRender/ListIconRender';
import { TextGenericWithSkin } from '../../TextGeneric/TextGenericWithSkin';
import { useTheme } from '../../../lib/theme/ThemeProvider';
import './ListItem.css';
import { useInternalVerticalAlignWithSkin } from '../../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { isModifierEvent } from '../../../lib/dom/isModifierEvent/isModifierEvent';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { EmptyIcon } from '../../../icons/EmptyIcon';

const cls = cn('ListItem');

export interface WithoutIdProps {
    /**
     * Действие при нажатии на элемент. Если не пусто, то элемент становится интерактивным и поддерживает ховер
     * @returns void
     */
    onClick?: () => void;
}

export interface WithIdProps<IdType extends string = string> {
    /**
     * id элемента, будет передан в обработчик клика
     * @example `<ListItem id="a" onClick={id => console.log(id)} />`
     */
    id: IdType;
    /**
     * Действие при нажатии на элемент. Если не пусто, то элемент становится интерактивным и поддерживает ховер
     * @returns void
     */
    onClick?: (id: IdType) => void;
}

export type ListItemProps<IdType extends string = never> = Omit<ListBaseItemProps, 'children'> &
    HelpHandler &
    TestProps &
    IsNever<IdType, WithoutIdProps, WithIdProps<IdType>> &
    HoverEvents &
    Loggable & {
        /**
         * Cодержимое элемента
         */
        children: ReactNode;

        /**
         * Дополнительная подпись к содержимому элемента
         */
        caption?: ReactNode;

        /**
         * Флаг отключенности элемента.
         * Отвечает за цвет текста, дефолтный цвет иконки и возможность взаимодействия с элементом
         */
        isDisabled?: boolean;

        /**
         * Иконка, которую нужно отрисовать слева от основного контента. При отрисовке передается нужный размер и цвет
         * @example <ListItem iconLeft={IconAttach}>content</ListItem>
         * @example <ListItem iconLeft={props => <IconErrorFilled {...props} />}>content</ListItem>
         */
        iconLeft?: ComponentType<ColorlessIconProps>;

        /**
         * Иконка, которую нужно отрисовать справа от основного контента. При отрисовке передается нужный размер и цвет
         * @example <ListItem iconRight={IconAttach}>content</ListItem>
         * @example <ListItem iconRight={props => <IconErrorFilled {...props} />}>content</ListItem>
         */
        iconRight?: ComponentType<ColorlessIconProps>;

        /**
         * Флаг для резервации места под иконку справа
         */
        iconRightReserveSpace?: boolean;

        /**
         * !!Для иконок используйте iconLeft!!
         * Кастомное содержимое слева от основного контента, имеет приоритет над `iconLeft`
         * @example <ListItem addonLeft={<Image width={40} height={40} alt="image" />}>content</ListItem>
         */
        addonLeft?: ReactNode;

        /**
         * !!Для иконок используйте iconRight!!
         * Кастомное содержимое справа от основного контента
         * @default иконка открытия в новой вкладке для ссылок
         */
        addonRight?: ReactNode;

        /**
         * Лейбл, рисующийся справа от основного контента
         * @example <ListItem label={props => <Label {...props}>новинка</Label>}>content</ListItem>
         */
        label?: FunctionComponent<LabelProps>;

        /**
         * Флаг, отвечающий за обрезку текста в элементе.
         * Если `true`, то текст не переносится и обрезается с многоточием
         */
        isTruncated?: boolean;

        /**
         * Адрес, на который нужно перейти при нажатии на элемент. Превращает элемент в нативную ссылку
         * @example <ListItem href="https://ya.ru">follow link</ListItem>
         */
        href?: string;

        /**
         * Атрибут target для ссылки
         */
        target?: HTMLAttributeAnchorTarget;

        /**
         * Роль элемента. По умолчанию используется `listitem`, но для использования в Select лучше передать `option`.
         * @default listitem
         */
        role?: AriaRole;

        /**
         * Стили для использования drag and drop
         */
        draggableStyle?: CSSProperties;
    };

/**
 * Компонент для одиночного элемента списка. Может быть интерактивным
 */
export const ListItem = genericForwardRef(function ListItem<T extends string = never>(
    props: ListItemProps<T>,
    forwardedRef: ForwardedRef<HTMLLIElement>,
) {
    const {
        children,
        href,
        draggableStyle,
        target,
        isDisabled,
        caption,
        isTruncated,
        help,
        iconLeft: IconLeft,
        iconRight,
        iconRightReserveSpace,
        addonLeft: propsAddonLeft,
        addonRight: propsAddonRight,
        label,
        className,
        role = 'listitem',
        'data-testid': dataTestId = 'ListItem',
    } = props;

    const contextValue = useContext(ListContext);
    const { size, onItemClick } = contextValue;
    const ref = useRef(null);
    const { LogProvider, logClick } = useLogger('ListItem', props.logNode);

    const isLink = Boolean(href) && !isDisabled;
    const isInteractive =
        Boolean(props.onClick) ||
        Boolean(href) ||
        Boolean(props.onHoverStart) ||
        Boolean(props.onHoverEnd) ||
        Boolean(props.onHoverChange);
    const isInteractionDisabled = isDisabled || !isInteractive;

    const handleClick = 'id' in props ? () => props.onClick?.(props.id) : () => props.onClick?.();
    const onClick = () => {
        logClick?.();
        onItemClick?.();
        handleClick();
    };

    const { pressProps, isPressed } = usePress({
        onPress: onClick,
        isDisabled: isInteractionDisabled,
    });

    const linkNavigateEvent = useMemo(() => (isMobile() ? 'onPress' : 'onPressEnd'), []);
    const router = useRouter();

    /**
     * TODO DIRECT-247892 Использовать useLink для ссылок в ListItem.
     * Переход по ссылке должен обрабатываться внутри useLink штатными средствами.
     * Нужно удалить логику перехода по ссылке и onClickCapture.
     * Сейчас это мешает сделать баг:
     * - Внутри useLink переход обрабатывается на onClick
     * - Закрытие шторки обрабатывается по press на оверлей
     * Это позволяет переходить по ссылкам сквозь оверлей, если использовать чуть более долгий тап на мобильных.
     * Стреляет во вложенных меню сайдбара на мобильных, см. тикет выше.
     */
    const { linkProps, isPressed: isLinkPressed } = useLink(
        {
            href,
            [linkNavigateEvent]: (event: PressEvent) => {
                onClick();

                if (!href || isInteractionDisabled || isModifierEvent(event)) {
                    return;
                }

                if (ref.current && !router.isNative && shouldClientNavigate(ref.current, event)) {
                    router.open(ref.current, event, href, undefined);
                } else if (typeof window !== 'undefined') {
                    window.open(href, target ?? '_self');
                }
            },
            isDisabled: isInteractionDisabled,
            elementType: isLink ? 'a' : 'span',
        },
        ref,
    );
    const onLinkClickCapture = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
        if (!isModifierEvent(event)) {
            event.preventDefault();
        }
    }, []);

    const { hoverProps, isHovered } = useHover({ ...props, isDisabled: false });
    const textPreset = getTextPresetWithSkin(props);
    const { addonClasses: labelClass } = useInternalVerticalAlignWithSkin({
        textSize: textPreset,
        addonType: 'icon',
    });
    const addonLeft = useMemo(() => {
        if (propsAddonLeft) {
            return propsAddonLeft;
        }

        if (IconLeft) {
            return <ListIconRender icon={IconLeft} isDisabled={isDisabled} />;
        }

        return null;
    }, [IconLeft, isDisabled, propsAddonLeft]);
    const addonRight = useMemo(() => {
        if (propsAddonRight) {
            return propsAddonRight;
        }

        if (iconRight || iconRightReserveSpace) {
            return <ListIconRender icon={iconRight ?? EmptyIcon} isDisabled={isDisabled} />;
        }

        if (href && target === '_blank') {
            return (
                <ListIconRender
                    icon={IconOpenInNewTab}
                    role="supplementary"
                    className={cls('new-tab', { visible: isMobile() || (isHovered && !isDisabled) })}
                />
            );
        }

        return null;
    }, [href, iconRight, iconRightReserveSpace, isDisabled, isHovered, propsAddonRight, target]);

    const { innerLabelSize } = useTheme().sizeTokens[size];
    const renderedLabel = useMemo(() => label?.({ size: innerLabelSize }), [innerLabelSize, label]);
    const colorSkinClass = getColorSkin(isDesktop() ? 'gray-transparent' : 'gray-neutral', {
        isHovered: !isInteractionDisabled && (isPressed || isLinkPressed || isHovered),
        isDisabled,
    });

    let content = (
        <TextGenericWithSkin
            className={cls('content')}
            textSize={textPreset}
            skinSize={size}
            caption={caption}
            color="current"
            isTruncated={isTruncated}
            help={help}
        >
            {children}
        </TextGenericWithSkin>
    );

    if (addonLeft || addonRight || label) {
        content = (
            <HStack gap={'current-geometry'} alignItems="flex-start" flexWrap="nowrap">
                {addonLeft}
                {content}
                {renderedLabel && <VStack className={labelClass}>{renderedLabel}</VStack>}
                {addonRight}
            </HStack>
        );
    }

    if (isInteractive) {
        if (isLink) {
            content = (
                <a
                    {...mergeProps(hoverProps, linkProps)}
                    href={href}
                    target={target}
                    onClickCapture={onLinkClickCapture}
                    className={cls('link')}
                    ref={ref}
                    data-testid={dataTestId}
                    role={role}
                >
                    {content}
                </a>
            );
        } else {
            content = (
                <div {...mergeProps(pressProps, hoverProps)} role={role} data-testid={dataTestId}>
                    {content}
                </div>
            );
        }
    }

    return (
        <LogProvider>
            <ListBaseItem
                className={cls(
                    {
                        hovered: isHovered,
                        pressed: isPressed || isLinkPressed,
                        disabled: isInteractionDisabled,
                        size,
                        'has-addon-left': Boolean(addonLeft),
                        'has-addon-right': Boolean(addonRight) || Boolean(renderedLabel),
                    },
                    [className, colorSkinClass],
                )}
                data-testid={isInteractive ? `${dataTestId}.BaseItem` : dataTestId}
                isDisabled={isDisabled}
                role={isInteractive ? 'none' : role}
                ref={forwardedRef}
                draggableStyle={draggableStyle}
            >
                {content}
            </ListBaseItem>
        </LogProvider>
    );
});
